// Web-exclusive Job History screen
// Displays completed, cancelled, and expired job applications with filtering

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Pressable, Image } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Building2, Briefcase, Globe, Clock, FileEdit, ChevronDown, Check, AlertTriangle, MapPin, DollarSign, Info } from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius } from '../../constants/webStyles';
import { getApplications } from '../../services/job';
import { PLACEHOLDER_PROFILE_URL } from '../../constants/placeholderImage';
import { JOB_TYPES, convertIndexToApiId } from '../../constants/jobTypes';
import { getCountryData } from '../../constants/countries';
import {
  isApplicationExpired,
  isEffectivelyCompleted,
  deriveApplicationDisplayStatus,
} from '../../utils/jobApplicationStatus';
import { useIsMobile } from '../../hooks/useIsMobile';

// Status badge colors matching HTML reference
const statusBadgeColors = {
  completed: { bg: '#d1f4e0', text: '#0d9f6e' },
  cancelled: { bg: '#fee2e2', text: '#991b1b' },
  expired: { bg: '#fef3c7', text: '#92400e' },
  pending: { bg: '#fef3c7', text: '#92400e' },
  approved: { bg: '#dbeafe', text: '#1d4ed8' },
};

const INTERNATIONAL_API_ID = 4; // matches JOB_TYPES International apiId

// Resolve a job's category apiId honoring the app-wide convention:
// international is flag-driven (see Details.web.jsx / JobListCard.jsx);
// otherwise fall back to type (Locum = 0 default).
const getJobApiId = (job) => {
  if (job.international) return INTERNATIONAL_API_ID;
  return parseInt(job.type, 10) || 0;
};

// Check if job is applied status (for non-locum history)
const isAppliedHistoryStatus = (job) => {
  const statusCandidates = [
    job?.job_application_status_code,
    job?.job_application_status,
    job?.status,
  ];
  return statusCandidates.some((value) => {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'applied';
    }
    return [null, 0, 1].includes(value);
  });
};

const JobHistoryScreen = () => {
  const navigation = useNavigation();
  const { isDoctor } = useSelector((state) => state.user);
  const isMobile = useIsMobile();

  // Filter states
  const [selectedJobType, setSelectedJobType] = useState(0); // 0 = All/Locum
  const [selectedStatus, setSelectedStatus] = useState(0); // 0 = All
  const [sortBy, setSortBy] = useState('latest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Fetch applications
  const { data: applicationsData, isLoading, error } = useQuery({
    queryKey: ['applications', { query: '' }],
    queryFn: getApplications,
  });

  // Categories with icons (matching HomeFacility)
  const categories = [
    { id: 0, label: 'Locum', Icon: Building2 },
    { id: 1, label: 'Full-Time', Icon: Briefcase },
    { id: 2, label: 'International', Icon: Globe },
    { id: 3, label: 'Temporary', Icon: Clock },
    { id: 4, label: 'Contract', Icon: FileEdit },
  ];

  // Status filter options (same for all job types)
  const statusOptions = useMemo(() => ([
    { id: 'all', name: 'All' },
    { id: 'completed', name: 'Completed' },
    { id: 'cancelled', name: 'Cancelled' },
    { id: 'expired', name: 'Expired' },
  ]), []);

  // Process and filter history jobs
  const historyJobs = useMemo(() => {
    const applications = applicationsData?.data?.data || [];

    // First filter to history-worthy jobs
    let filtered = applications.filter((job) => {
      // Completed jobs (incl. checked-in jobs whose shift has ended)
      if (isEffectivelyCompleted(job)) return true;
      // Cancelled jobs
      if (job.job_application_status_code === -2) return true;
      // Expired jobs (past end date, never checked in)
      if (isApplicationExpired(job)) return true;
      // For non-locum: include applied status
      const jobType = getJobApiId(job);
      if (jobType > 0 && isAppliedHistoryStatus(job)) return true;
      return false;
    });

    // Derive canonical display status
    filtered = filtered.map((job) => {
      const displayStatus = deriveApplicationDisplayStatus(job);
      return {
        ...job,
        is_expired: displayStatus === 'expired',
        display_status: displayStatus,
      };
    });

    // Filter by job type (always filter, matching HomeFacility approach)
    const selectedApiId = convertIndexToApiId(selectedJobType);
    filtered = filtered.filter(job => getJobApiId(job) === selectedApiId);

    // Filter by status
    const selectedStatusOption = statusOptions[selectedStatus];
    if (selectedStatusOption && selectedStatusOption.id !== 'all') {
      filtered = filtered.filter(job => {
        switch (selectedStatusOption.id) {
          case 'completed':
            return job.display_status === 4;
          case 'cancelled':
            return job.display_status === -2;
          case 'expired':
            return job.display_status === 'expired';
          default:
            return true;
        }
      });
    }

    // Sort jobs
    filtered.sort((a, b) => {
      const dateA = moment(a.end_timestamp || a.job_end_timestamp);
      const dateB = moment(b.end_timestamp || b.job_end_timestamp);
      return sortBy === 'latest' ? dateB.unix() - dateA.unix() : dateA.unix() - dateB.unix();
    });

    return filtered;
  }, [applicationsData, selectedJobType, selectedStatus, statusOptions, sortBy]);

  // Get status badge style
  const getStatusBadgeStyle = (status) => {
    if (status === 4 || status === 'completed') return statusBadgeColors.completed;
    if (status === -2 || status === 'cancelled') return statusBadgeColors.cancelled;
    if (status === 'expired') return statusBadgeColors.expired;
    return statusBadgeColors.pending;
  };

  // Get status label
  const getStatusLabel = (job) => {
    if (job.display_status === 'expired') return 'Expired';
    if (job.display_status === 4) return 'Completed';
    if (job.display_status === -2) return 'Cancelled';
    return 'Pending';
  };

  // Format price
  const formatPrice = (job) => {
    const currency = getCountryData(job.country || 'MY').currency;
    if (job.price_max && job.price_max > job.price) {
      return `${currency} ${job.price} - ${job.price_max}`;
    }
    return `${currency} ${job.price}`;
  };

  // Get job type name
  const getJobTypeName = (typeId) => {
    const type = JOB_TYPES.find(t => t.apiId === parseInt(typeId));
    return type?.name || 'Locum';
  };

  return (
    <WebLayout>
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Page Header with Sort Dropdown */}
        <View style={[styles.pageHeader, isMobile && styles.pageHeaderMobile]}>
          <View style={styles.headerTitleSection}>
            <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Job History</Text>
            <Text style={styles.pageSubtitle}>View your past applications and job activities</Text>
          </View>

          {/* Sort Dropdown */}
          <View style={styles.sortDropdownWrapper}>
            <Pressable
              style={styles.sortPill}
              onPress={() => setShowSortDropdown(!showSortDropdown)}
            >
              <Text style={styles.sortPillText}>Sort by: </Text>
              <Text style={styles.sortPillValue}>{sortBy === 'latest' ? 'Latest' : 'Oldest'}</Text>
              <View style={[styles.sortArrow, showSortDropdown && styles.sortArrowRotated]}>
                <ChevronDown size={14} color={webColors.textSecondary} />
              </View>
            </Pressable>

            {showSortDropdown && (
              <View style={styles.sortDropdownMenu}>
                <Pressable
                  style={[styles.sortDropdownItem, sortBy === 'latest' && styles.sortDropdownItemActive]}
                  onPress={() => { setSortBy('latest'); setShowSortDropdown(false); }}
                >
                  <Text style={[styles.sortDropdownItemText, sortBy === 'latest' && styles.sortDropdownItemTextActive]}>
                    Latest
                  </Text>
                  {sortBy === 'latest' && <Check size={14} color={webColors.primary} />}
                </Pressable>
                <Pressable
                  style={[styles.sortDropdownItem, sortBy === 'oldest' && styles.sortDropdownItemActive]}
                  onPress={() => { setSortBy('oldest'); setShowSortDropdown(false); }}
                >
                  <Text style={[styles.sortDropdownItemText, sortBy === 'oldest' && styles.sortDropdownItemTextActive]}>
                    Oldest
                  </Text>
                  {sortBy === 'oldest' && <Check size={14} color={webColors.primary} />}
                </Pressable>
              </View>
            )}
          </View>
        </View>

        {/* Job Type Filter Pills */}
        <View style={styles.filterContainer}>
          {categories.map((cat, index) => (
            <TouchableOpacity
              key={cat.id}
              style={[styles.filterPill, selectedJobType === index && styles.filterPillActive]}
              onPress={() => setSelectedJobType(index)}
            >
              <View style={styles.filterPillIcon}>
                <cat.Icon size={14} color={selectedJobType === index ? '#be185d' : webColors.textSecondary} />
              </View>
              <Text style={[styles.filterPillText, selectedJobType === index && styles.filterPillTextActive]}>
                {cat.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Content Section */}
        <View style={[styles.contentSection, isMobile && styles.contentSectionMobile]}>
          {/* Status Sub-Pills */}
          <View style={styles.subPillsContainer}>
            {statusOptions.map((status, index) => (
              <TouchableOpacity
                key={status.id}
                style={[styles.subPill, selectedStatus === index && styles.subPillActive]}
                onPress={() => setSelectedStatus(index)}
              >
                <Text style={[styles.subPillText, selectedStatus === index && styles.subPillTextActive]}>
                  {status.name}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Content Area */}
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={webColors.primary} />
              <Text style={styles.loadingText}>Loading job history...</Text>
            </View>
          ) : error ? (
            <View style={styles.errorState}>
              <View style={styles.errorIcon}>
                <AlertTriangle size={48} color="#92400e" />
              </View>
              <Text style={styles.errorTitle}>Unable to load job history</Text>
              <Text style={styles.errorText}>Please check your connection and try again</Text>
            </View>
          ) : historyJobs.length === 0 ? (
            <EmptyState
              jobType={categories[selectedJobType]?.label || 'Locum'}
              status={statusOptions[selectedStatus]?.name}
              navigation={navigation}
            />
          ) : (
            <View style={styles.jobsList}>
              {historyJobs.map((job, index) => (
                <HistoryJobCard
                  key={job.uuid || index}
                  job={job}
                  isDoctor={isDoctor}
                  navigation={navigation}
                  getStatusBadgeStyle={getStatusBadgeStyle}
                  getStatusLabel={getStatusLabel}
                  formatPrice={formatPrice}
                  getJobTypeName={getJobTypeName}
                />
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    </WebLayout>
  );
};

// History Job Card Component
const HistoryJobCard = ({ job, isDoctor, navigation, getStatusBadgeStyle, getStatusLabel, formatPrice, getJobTypeName }) => {
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);

  const statusStyle = getStatusBadgeStyle(job.display_status);
  const statusLabel = getStatusLabel(job);
  const clinicName = job.clinic?.name || job.facility_name || 'Facility';
  const clinicCity = job.clinic?.city || job.facility_city || '';
  const jobTitle = job.title || job.job_title || 'Job Position';

  // Get initials for logo
  const getInitials = (name) => {
    if (!name) return 'F';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Calculate duration in hours
  const getDuration = () => {
    if (!job.start_timestamp || !job.end_timestamp) return null;
    const start = moment(job.start_timestamp);
    const end = moment(job.end_timestamp);
    const hours = end.diff(start, 'hours');
    return hours > 0 ? `${hours} hours` : null;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    return moment(date).format('MMM D, YYYY');
  };

  const handlePress = () => {
    navigation.navigate(isDoctor ? 'DetailsScreen' : 'DetailsScreen', {
      data: {
        ...job,
        uuid: job.uuid || job.job_uuid,
        status: job.job_application_status_code,
        job_application: { uuid: job.uuid },
      },
    });
  };

  return (
    <Pressable
      style={[styles.jobCard, isHovered && styles.jobCardHovered, isMobile && styles.jobCardMobile]}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={handlePress}
    >
      {/* Logo */}
      <View style={styles.jobLogo}>
        <Image
          source={{ uri: job.clinic?.profile_image || PLACEHOLDER_PROFILE_URL }}
          style={styles.jobLogoImage}
        />
      </View>

      {/* Job Info */}
      <View style={styles.jobInfo}>
        <Text style={styles.jobTitle}>{jobTitle}</Text>
        <Text style={styles.jobCompany}>{clinicName}</Text>

        <View style={styles.jobMeta}>
          {clinicCity && (
            <View style={styles.jobMetaItem}>
              <MapPin size={14} color={webColors.textSecondary} />
              <Text style={styles.jobMetaText}>{clinicCity}</Text>
            </View>
          )}
          {job.price > 0 && (
            <View style={styles.jobMetaItem}>
              <DollarSign size={14} color={webColors.textSecondary} />
              <Text style={styles.jobMetaText}>{formatPrice(job)}</Text>
            </View>
          )}
          {getDuration() && (
            <View style={styles.jobMetaItem}>
              <Clock size={14} color={webColors.textSecondary} />
              <Text style={styles.jobMetaText}>{getDuration()}</Text>
            </View>
          )}
        </View>

        {/* Status Badge */}
        <View style={[styles.statusBadge, { backgroundColor: statusStyle.bg }]}>
          <Text style={[styles.statusBadgeText, { color: statusStyle.text }]}>{statusLabel}</Text>
        </View>

        {/* Date Info */}
        <Text style={styles.jobDateInfo}>
          {statusLabel === 'Completed' ? 'Completed on ' :
           statusLabel === 'Cancelled' ? 'Cancelled on ' :
           statusLabel === 'Expired' ? 'Expired on ' : ''}
          {formatDate(job.end_timestamp || job.created_at)}
        </Text>
      </View>

      {/* View Button */}
      <TouchableOpacity style={styles.viewButton} onPress={handlePress}>
        <Text style={styles.viewButtonText}>View</Text>
      </TouchableOpacity>
    </Pressable>
  );
};

// Empty State Component
const EmptyState = ({ jobType, status, navigation }) => {
  const getMessage = () => {
    if (jobType !== 'Locum' && status !== 'All') {
      return `No ${status.toLowerCase()} ${jobType} jobs in history`;
    }
    if (status !== 'All') {
      return `No ${status.toLowerCase()} jobs in history`;
    }
    if (jobType !== 'Locum') {
      return `No ${jobType} jobs in history`;
    }
    return 'No jobs in history';
  };

  return (
    <View style={styles.emptyState}>
      <View style={styles.emptyStateIcon}>
        <Info size={32} color={webColors.textMuted} />
      </View>
      <Text style={styles.emptyStateText}>{getMessage()}</Text>
      <TouchableOpacity
        style={styles.emptyStateButton}
        onPress={() => navigation.navigate('Jobs')}
      >
        <Text style={styles.emptyStateButtonText}>Browse Jobs</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  pageHeader: {
    zIndex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  headerTitleSection: {
    flex: 1,
  },
  pageTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textSecondary,
  },

  // Sort Dropdown
  sortDropdownWrapper: {
    position: 'relative',
    zIndex: 100,
  },
  sortPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 20,
    gap: 4,
  },
  sortPillText: {
    fontSize: 13,
    color: webColors.textSecondary,
  },
  sortPillValue: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  sortArrow: {
    marginLeft: 4,
  },
  sortArrowRotated: {
    transform: [{ rotate: '180deg' }],
  },
  sortDropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    minWidth: 150,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 4,
  },
  sortDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  sortDropdownItemActive: {},
  sortDropdownItemText: {
    fontSize: 14,
    color: webColors.textSecondary,
  },
  sortDropdownItemTextActive: {
    color: webColors.primary,
    fontWeight: '600',
  },

  // Filter Pills
  filterContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 24,
  },
  filterPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: 100,
  },
  filterPillActive: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  filterPillIcon: {
    width: 14,
    height: 14,
  },
  filterPillText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  filterPillTextActive: {
    color: '#be185d',
  },

  // Content Section
  contentSection: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    padding: 24,
  },

  // Sub Pills
  subPillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 24,
  },
  subPill: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: 10,
  },
  subPillActive: {
    backgroundColor: webColors.primaryLight,
    borderColor: webColors.primary,
  },
  subPillText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  subPillTextActive: {
    color: '#be185d',
    fontWeight: '600',
  },

  // Loading State
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: webColors.textMuted,
  },

  // Error State
  errorState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  errorIcon: {
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    color: webColors.textMuted,
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyStateIcon: {
    width: 80,
    height: 80,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyStateText: {
    fontSize: 16,
    color: webColors.textMuted,
    marginBottom: 24,
  },
  emptyStateButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
  },
  emptyStateButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Jobs List
  jobsList: {},

  // Job Card
  jobCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
    padding: 28,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.borderLight,
    borderRadius: 20,
    marginBottom: 16,
  },
  jobCardHovered: {
    borderColor: webColors.primary,
    shadowColor: webColors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    transform: [{ translateY: -2 }],
  },
  jobLogo: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  jobLogoImage: {
    width: 64,
    height: 64,
    borderRadius: 16,
  },
  jobLogoText: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.primary,
  },
  jobInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 6,
  },
  jobCompany: {
    fontSize: 15,
    fontWeight: '500',
    color: webColors.textLink,
    marginBottom: 12,
  },
  jobMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    marginBottom: 16,
  },
  jobMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  jobMetaText: {
    fontSize: 14,
    color: webColors.textSecondary,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 10,
    marginBottom: 8,
  },
  statusBadgeText: {
    fontSize: 13,
    fontWeight: '600',
  },
  jobDateInfo: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 4,
  },
  viewButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.primary,
    borderRadius: 10,
    alignSelf: 'center',
  },
  viewButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
  },

  // Mobile variants
  pageHeaderMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
  },
  contentSectionMobile: {
    padding: 16,
  },
  pageTitleMobile: {
    fontSize: 22,
  },
  jobCardMobile: {
    flexDirection: 'column',
    padding: 18,
    gap: 16,
  },
});

export default JobHistoryScreen;
